import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { supabase } from '@/integrations/supabase/client'
import { useOrgPermissions } from '@/hooks/useOrgPermissions'
import { useToast } from '@/hooks/use-toast'
import { logActivity } from '@/lib/activityLog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { ArrowLeft, Plus, Loader2, Copy, Check, ChevronsUpDown, Download, Trash2, CalendarIcon, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ConorbitLoader } from '@/components/ui/ConorbitLoader'

// ── Constants ──────────────────────────────────────────────────────────────

const ECOWAS_COUNTRIES = [
  'Benin', 'Burkina Faso', 'Cape Verde', "Côte d'Ivoire", 'Gambia', 'Ghana',
  'Guinea', 'Guinea-Bissau', 'Liberia', 'Mali', 'Mauritania', 'Niger',
  'Nigeria', 'Senegal', 'Sierra Leone', 'Togo',
]

const STATUS_STEPS = ['draft', 'selection_pending', 'invitations_sent', 'active', 'completed'] as const
const STEP_LABELS: Record<string, string> = {
  draft: 'Draft',
  selection_pending: 'Selection Pending',
  invitations_sent: 'Invitations Sent',
  active: 'Active',
  completed: 'Completed',
}

const STATUS_BADGE: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600 border-gray-300',
  selection_pending: 'bg-amber-100 text-amber-700 border-amber-300',
  invitations_sent: 'bg-blue-100 text-blue-700 border-blue-300',
  active: 'bg-[#E1F5EE] text-[#006B3C] border-[#006B3C]/30',
  completed: 'bg-[#0D2240] text-white border-transparent',
  cancelled: 'bg-red-100 text-red-700 border-red-300',
}


const OVERRIDE_CATEGORIES = [
  { value: 'operational', label: 'Operational: outlet is already covering independently' },
  { value: 'strategic', label: 'Strategic: specific editorial coverage needed from another outlet' },
  { value: 'logistical', label: 'Logistical: journalist has confirmed unavailability' },
  { value: 'disciplinary', label: 'Disciplinary: active restriction or conduct issue pending' },
  { value: 'balancing', label: 'Balancing: adjusting gender or regional representation' },
  { value: 'other', label: 'Other: explain in notes below (required)' },
]

const ACTION_DISPLAY: Record<string, { icon: string; label: string; colour: string }> = {
  event_created:          { icon: '📋', label: 'Event created',              colour: 'text-slate-600' },
  ai_run:                 { icon: '🤖', label: 'AI selection computed',      colour: 'text-blue-600'  },
  cco_override:           { icon: '⚠️', label: 'Institution Admin override',      colour: 'text-amber-600' },
  cco_add:                { icon: '➕', label: 'Journalist added by Admin',       colour: 'text-green-600' },
  codes_generated:        { icon: '🔑', label: 'Attendance codes generated', colour: 'text-purple-600'},
  invitations_dispatched: { icon: '✉️', label: 'Invitations dispatched',     colour: 'text-blue-600'  },
  attendance_recorded:    { icon: '📍', label: 'Attendance recorded',        colour: 'text-green-600' },
  restriction_applied:    { icon: '🔒', label: 'Restriction applied',        colour: 'text-red-600'   },
  restriction_lifted:     { icon: '🔓', label: 'Restriction lifted',         colour: 'text-green-600' },
}

// ── New Event Schema ───────────────────────────────────────────────────────

const eventSchema = z.object({
  name:              z.string().min(3, 'At least 3 characters'),
  event_type:        z.enum(['plenary','committee','briefing','summit','workshop','other']),
  start_at:          z.string().min(1, 'Required'),
  start_time:        z.string().optional(),
  end_at:            z.string().min(1, 'Required'),
  end_time:          z.string().optional(),
  host_country:      z.string().min(1, 'Required'),
  venue:             z.string().min(1, 'Required'),
  venue_city:        z.string().min(1, 'Required'),
  local_capacity:    z.coerce.number().min(0),
  regional_capacity: z.coerce.number().min(0),
  languages:         z.array(z.string()).min(1, 'Select at least one language'),
  special_criteria:  z.string().optional(),
}).refine(v => (v.local_capacity + v.regional_capacity) >= 1, {
  message: 'Enter at least one local or regional journalist needed',
  path: ['local_capacity'],
})
type EventForm = z.infer<typeof eventSchema>

// ── EventsView (main export) ───────────────────────────────────────────────

export function EventsView() {
  const { orgId, can, loading } = useOrgPermissions()
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)

  if (loading) return <ConorbitLoader variant="inline" />;

  if (!orgId) return (
    <Card><CardContent className="py-12 text-center text-muted-foreground">
      You are not a member of any organisation.
    </CardContent></Card>
  )

  if (!can('events', 'view')) return (
    <Card><CardContent className="py-12 text-center text-muted-foreground">
      You do not have permission to access Events.
    </CardContent></Card>
  )

  if (selectedEventId) {
    return (
      <EventDetailMode
        eventId={selectedEventId}
        orgId={orgId}
        canEdit={can('events', 'edit')}
        onBack={() => setSelectedEventId(null)}
      />
    )
  }

  return <EventListMode orgId={orgId} canEdit={can('events', 'edit')} onOpenEvent={setSelectedEventId} />
}

// ── EventListMode ──────────────────────────────────────────────────────────

function EventListMode({
  orgId, canEdit, onOpenEvent
}: { orgId: string; canEdit: boolean; onOpenEvent: (id: string) => void }) {
  const { toast } = useToast()
  const [newOpen, setNewOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null)
  const [deleting, setDeleting] = useState(false)

  const { data: events, isLoading, refetch } = useQuery({
    queryKey: ['events', orgId],
    queryFn: async () => {
      const { data } = await (supabase
        .from('events' as any)
        .select('*')
        .eq('org_id', orgId)
        .order('start_at', { ascending: false }) as any)
      return (data || []) as any[]
    }
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Events</h1>
          <p className="text-justify text-muted-foreground">Manage parliamentary events and journalist coverage selection.</p>
        </div>
        {canEdit && (
          <Button style={{ backgroundColor: '#006B3C' }} className="text-white hover:opacity-90" onClick={() => setNewOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Event
          </Button>
        )}
      </div>

      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow style={{ backgroundColor: '#0D2240' }}>
              {['Reference', 'Event', 'Dates', 'Country', 'Local', 'Regional', 'Status', ''].map(h => (
                <TableHead key={h} style={{ color: '#F0E4B8' }} className="font-semibold">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12">
                <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
              </TableCell></TableRow>
            ) : (events || []).length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                No events yet.{canEdit ? ' Create the first one.' : ''}
              </TableCell></TableRow>
            ) : (events || []).map((ev: any, idx: number) => (
              <TableRow key={ev.id} className={idx % 2 === 1 ? 'bg-[#F7F5F0]' : ''}>
                <TableCell>
                  <span className="font-mono text-sm font-semibold" style={{ color: '#006B3C' }}>
                    {ev.event_reference ?? ''}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="font-medium">{ev.name}</div>
                  <Badge variant="secondary" className="text-xs capitalize mt-0.5">{ev.event_type}</Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {ev.start_at ? new Date(ev.start_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                  {ev.end_at && ev.end_at !== ev.start_at ? ` → ${new Date(ev.end_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}
                </TableCell>
                <TableCell className="text-sm">{ev.host_country}</TableCell>
                <TableCell className="text-sm text-[#0D2240]">{ev.local_capacity ?? '—'}</TableCell>
                <TableCell className="text-sm text-[#0D2240]">{ev.regional_capacity ?? '—'}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={`text-xs capitalize ${STATUS_BADGE[ev.status] ?? ''}`}>
                    {ev.status?.replace(/_/g, ' ')}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" onClick={() => onOpenEvent(ev.id)}>
                      Open →
                    </Button>
                    {canEdit && (
                      <Button
                        size="sm" variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteTarget(ev)}
                        aria-label="Delete event"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <NewEventSheet
        orgId={orgId}
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={(id) => { refetch(); onOpenEvent(id) }}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={o => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the event and any AI recommendations, admin selections, attendance codes, and audit history attached to it. This cannot be undone. If passes or accreditation requests have already been issued for this event, deletion will be blocked — cancel the event instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Go back</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
              onClick={async () => {
                setDeleting(true)
                try {
                  const { error } = await (supabase.from('events' as any).delete().eq('id', deleteTarget.id) as any)
                  if (error) throw error
                  toast({ title: 'Event deleted.' })
                  setDeleteTarget(null)
                  refetch()
                } catch (err: any) {
                  toast({
                    title: 'Could not delete event',
                    description: err.message?.includes('foreign key')
                      ? 'Passes, invitations, or coverage records already exist for this event. Cancel it instead of deleting.'
                      : err.message,
                    variant: 'destructive',
                  })
                } finally {
                  setDeleting(false)
                }
              }}
            >
              {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete Event
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// ── NewEventSheet ──────────────────────────────────────────────────────────

// Fixed, non-configurable — the AI fairness engine always weighs these the
// same way. Coverage is weighted highest so journalists who consistently
// give little or no coverage are naturally ranked lower and flagged.
const FIXED_WEIGHTS = { time_since: 0.25, attendance: 0.15, coverage: 0.30, beat: 0.15, geo: 0.15 }

function DatePickerField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false)
  const selected = value ? new Date(`${value}T00:00:00`) : undefined
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn('w-full justify-start text-left font-normal', !value && 'text-muted-foreground')}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {selected ? selected.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Pick a date'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => {
            if (!d) return
            const y = d.getFullYear()
            const m = String(d.getMonth() + 1).padStart(2, '0')
            const day = String(d.getDate()).padStart(2, '0')
            onChange(`${y}-${m}-${day}`)
            setOpen(false)
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )
}

function NewEventSheet({
  orgId, open, onClose, onCreated
}: { orgId: string; open: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const { toast } = useToast()
  const [submitting, setSubmitting] = useState(false)

  const form = useForm<EventForm>({
    resolver: zodResolver(eventSchema),
    defaultValues: {
      name: '', event_type: 'plenary', start_at: '', start_time: '', end_at: '', end_time: '',
      host_country: '', venue: '', venue_city: '',
      local_capacity: 0, regional_capacity: 10, languages: ['en'], special_criteria: '',
    }
  })

  const watchedLangs = form.watch('languages')

  function toggleLang(lang: string) {
    const current = form.getValues('languages')
    form.setValue('languages',
      current.includes(lang) ? current.filter(l => l !== lang) : [...current, lang],
      { shouldValidate: true }
    )
  }

  async function onSubmit(values: EventForm) {
    setSubmitting(true)
    try {
      const { start_time, end_time, ...rest } = values
      const { data, error } = await (supabase
        .from('events' as any)
        .insert({
          ...rest,
          start_at: `${values.start_at}T${start_time || '00:00'}:00`,
          end_at: `${values.end_at}T${end_time || '00:00'}:00`,
          org_id: orgId,
          selection_weights: FIXED_WEIGHTS,
          coverage_scope: 'both',
          physical_capacity: values.local_capacity + values.regional_capacity,
          online_capacity: 0,
        })
        .select('id')
        .single() as any)
      if (error) throw error
      await logActivity({
        orgId,
        actionType: 'event_created',
        description: `Event created: ${values.name}`,
        metadata: { event_id: data.id },
      })
      toast({ title: 'Event created', description: values.name })
      form.reset()
      onClose()
      onCreated(data.id)
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={o => !o && onClose()}>
      <SheetContent side="right" className="w-[560px] max-w-full overflow-y-auto">
        <SheetHeader><SheetTitle>New Event</SheetTitle></SheetHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="mt-6 space-y-4">
            <FormField control={form.control} name="name" render={({ field }) => (
              <FormItem>
                <FormLabel>Event Name</FormLabel>
                <FormControl><Input {...field} placeholder="56th Ordinary Session, ECOWAS Parliament" /></FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="event_type" render={({ field }) => (
                <FormItem>
                  <FormLabel>Type</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                    <SelectContent>
                      {['plenary','committee','briefing','summit','workshop','other'].map(t => (
                        <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            <p className="text-justify text-xs text-muted-foreground -mt-2 px-0.5">
              Local journalists are those based in the host country; regional journalists are everyone else. The host country is automatically excluded from the regional pool. Each is scored and ranked independently for fairness.
            </p>

            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="local_capacity" render={({ field }) => (
                <FormItem>
                  <FormLabel>Local journalists needed</FormLabel>
                  <FormControl><Input {...field} type="number" min={0} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="regional_capacity" render={({ field }) => (
                <FormItem>
                  <FormLabel>Regional journalists needed</FormLabel>
                  <FormControl><Input {...field} type="number" min={0} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="start_at" render={({ field }) => (
                <FormItem>
                  <FormLabel>Start Date</FormLabel>
                  <DatePickerField value={field.value} onChange={field.onChange} />
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="end_at" render={({ field }) => (
                <FormItem>
                  <FormLabel>End Date</FormLabel>
                  <DatePickerField value={field.value} onChange={field.onChange} />
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="start_time" render={({ field }) => (
                <FormItem>
                  <FormLabel>Start Time <span className="text-muted-foreground font-normal">(optional)</span></FormLabel>
                  <FormControl><Input {...field} type="time" /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="end_time" render={({ field }) => (
                <FormItem>
                  <FormLabel>End Time <span className="text-muted-foreground font-normal">(optional)</span></FormLabel>
                  <FormControl><Input {...field} type="time" /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            <FormField control={form.control} name="host_country" render={({ field }) => (
              <FormItem>
                <FormLabel>Host Country</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl><SelectTrigger><SelectValue placeholder="Select country" /></SelectTrigger></FormControl>
                  <SelectContent>
                    {ECOWAS_COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />

            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="venue" render={({ field }) => (
                <FormItem>
                  <FormLabel>Venue</FormLabel>
                  <FormControl><Input {...field} placeholder="ECOWAS Parliament Building" /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="venue_city" render={({ field }) => (
                <FormItem>
                  <FormLabel>City</FormLabel>
                  <FormControl><Input {...field} placeholder="Abuja" /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            <FormField control={form.control} name="languages" render={() => (
              <FormItem>
                <FormLabel>Event Languages</FormLabel>
                <div className="flex gap-3">
                  {[['en','English'],['fr','Français'],['pt','Português']].map(([code, label]) => (
                    <label key={code} className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={watchedLangs.includes(code)}
                        onChange={() => toggleLang(code)}
                        className="rounded"
                      />
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </div>
                <FormMessage />
              </FormItem>
            )} />

            <FormField control={form.control} name="special_criteria" render={({ field }) => (
              <FormItem>
                <FormLabel>Special Criteria <span className="text-muted-foreground font-normal">(optional)</span></FormLabel>
                <FormControl>
                  <Textarea {...field} placeholder="Any additional selection criteria for this event…" rows={3} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <p className="text-justify text-[11px] text-muted-foreground bg-muted rounded-lg px-3 py-2">
              Selection is always fair: every eligible journalist is scored on invite recency, attendance history, beat relevance, and geographic diversity — and journalists who give little or no coverage, or who lag behind the average, are flagged so the institution can see who isn't pulling their weight. This isn't configurable per event.
            </p>

            <div className="pt-4">
              <Button
                type="submit"
                disabled={submitting}
                style={{ backgroundColor: '#006B3C' }}
                className="w-full text-white hover:opacity-90 disabled:opacity-50"
              >
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create Event
              </Button>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}

// ── EventDetailMode ────────────────────────────────────────────────────────

function EventDetailMode({
  eventId, orgId, canEdit, onBack
}: { eventId: string; orgId: string; canEdit: boolean; onBack: () => void }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [runningAI, setRunningAI] = useState(false)
  const [generatingCodes, setGeneratingCodes] = useState(false)
  const [completingEvent, setCompletingEvent] = useState(false)
  const [showCompleteDialog, setShowCompleteDialog] = useState(false)
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const { data: event, refetch: refetchEvent } = useQuery({
    queryKey: ['event', eventId],
    queryFn: async () => {
      const { data } = await (supabase
        .from('events' as any)
        .select('*')
        .eq('id', eventId)
        .single() as any)
      return data as any
    }
  })

  const eventOrgId = event?.org_id ?? orgId

  const { data: journalists } = useQuery({
    queryKey: ['journalists-for-event', eventOrgId],
    queryFn: async () => {
      const { data: jData } = await (supabase
        .from('journalists' as any)
        .select('id, first_name, last_name, nationality, beats, seniority')
        .eq('org_id', eventOrgId)
        .eq('status', 'active')
        .order('last_name') as any)
      const journalists = (jData || []) as any[]
      if (journalists.length) {
        const ids = journalists.map((j: any) => j.id)
        const { data: affs } = await (supabase
          .from('journalist_affiliations' as any)
          .select('journalist_id, media_outlets(name)')
          .eq('org_id', eventOrgId)
          .eq('is_primary', true)
          .in('journalist_id', ids) as any)
        const affMap: Record<string, string> = {}
        ;(affs || []).forEach((a: any) => { affMap[a.journalist_id] = a.media_outlets?.name ?? '' })
        journalists.forEach((j: any) => { j.outlet_name = affMap[j.id] ?? null })
      }
      return journalists
    },
    enabled: !!eventOrgId
  })

  async function updateEventStatus(status: string) {
    const { error } = await (supabase
      .from('events' as any)
      .update({ status })
      .eq('id', eventId) as any)
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    } else {
      refetchEvent()
      queryClient.invalidateQueries({ queryKey: ['events', orgId] })
    }
  }

  async function handleRunAI() {
    setRunningAI(true)
    try {
      const result = await supabase.functions.invoke('compute-event-selection', {
        body: { event_id: eventId }
      })
      if (result.error) {
        // supabase-js only reports "non-2xx status code" for the error itself;
        // the actual message/diagnostics are in the response body.
        let message = result.error.message
        try {
          const body = await result.error.context?.json?.()
          if (body?.error) message = body.error
          if (body?.diagnostics) {
            const d = body.diagnostics
            message += ` (active: ${d.active_journalists_in_org}, after scope filter: ${d.after_coverage_scope_filter}, excluded by restrictions: ${d.excluded_by_active_restrictions}, event org_id: ${d.event_org_id}, journalists in DB (any org): ${d.total_journalists_any_org_any_status}, journalists in this org (any status): ${d.total_journalists_this_org_any_status})`
          }
        } catch { /* fall back to generic message */ }
        throw new Error(message)
      }
      const body = result.data as any
      if (body?.error) throw new Error(body.error)
      await updateEventStatus('selection_pending')
      queryClient.invalidateQueries({ queryKey: ['ai-recommendations', eventId] })
      toast({ title: 'AI Selection Complete', description: `${body?.recommended_count ?? 'Journalists'} ranked.` })
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' })
    } finally {
      setRunningAI(false)
    }
  }

  async function handleDispatch() {
    setGeneratingCodes(true)
    try {
      const result = await supabase.functions.invoke('generate-attendance-codes', {
        body: { event_id: eventId }
      })
      if (result.error) throw new Error(result.error.message)
      await updateEventStatus('invitations_sent')
      queryClient.invalidateQueries({ queryKey: ['station-codes', eventId] })
      toast({ title: 'Codes generated. Invitations dispatched.' })
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' })
    } finally {
      setGeneratingCodes(false)
    }
  }

  async function handleCompleteEvent() {
    setCompletingEvent(true)
    try {
      await supabase.functions.invoke('check-event-noshows', { body: { event_id: eventId } })
      await updateEventStatus('completed')
      toast({ title: 'Event completed.' })
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' })
    } finally {
      setCompletingEvent(false)
      setShowCompleteDialog(false)
    }
  }

  async function handleCancelEvent() {
    setCancelling(true)
    try {
      await updateEventStatus('cancelled')
      toast({ title: 'Event cancelled.' })
    } finally {
      setCancelling(false)
      setShowCancelDialog(false)
    }
  }

  async function handleDeleteEvent() {
    setDeleting(true)
    try {
      const { error } = await (supabase.from('events' as any).delete().eq('id', eventId) as any)
      if (error) throw error
      toast({ title: 'Event deleted.' })
      setShowDeleteDialog(false)
      onBack()
    } catch (err: any) {
      toast({
        title: 'Could not delete event',
        description: err.message?.includes('foreign key')
          ? 'Passes, invitations, or coverage records already exist for this event. Cancel it instead of deleting.'
          : err.message,
        variant: 'destructive',
      })
    } finally {
      setDeleting(false)
    }
  }

  if (!event) return <ConorbitLoader variant="inline" />;

  const statusIdx = STATUS_STEPS.indexOf(event.status as any)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3"
        >
          <ArrowLeft className="w-4 h-4" />
          All Events
        </button>
        <div className="flex items-start gap-3">
          <h1 className="text-2xl font-bold flex-1">{event.name}</h1>
          <Badge variant="outline" className={`capitalize text-xs ${STATUS_BADGE[event.status] ?? ''}`}>
            {event.status?.replace(/_/g, ' ')}
          </Badge>
        </div>
      </div>

      {/* Status stepper */}
      <div className="flex items-center gap-0">
        {STATUS_STEPS.map((step, i) => {
          const done = i <= statusIdx
          const current = i === statusIdx
          return (
            <div key={step} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center">
                <div className={cn(
                  'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors',
                  done ? 'bg-[#006B3C] border-[#006B3C] text-white' : 'bg-background border-muted text-muted-foreground',
                  current && 'ring-2 ring-[#006B3C]/30'
                )}>
                  {i + 1}
                </div>
                <span className="text-[10px] text-center mt-1 text-muted-foreground w-20 leading-tight">
                  {STEP_LABELS[step]}
                </span>
              </div>
              {i < STATUS_STEPS.length - 1 && (
                <div className={cn('flex-1 h-0.5 mx-1 mb-5', i < statusIdx ? 'bg-[#006B3C]' : 'bg-muted')} />
              )}
            </div>
          )
        })}
      </div>

      {/* Action buttons */}
      {canEdit && event.status !== 'cancelled' && event.status !== 'completed' && (
        <div className="flex items-center gap-2 flex-wrap">
          {event.status === 'draft' && (
            <Button
              style={{ backgroundColor: '#006B3C' }}
              className="text-white hover:opacity-90"
              onClick={handleRunAI}
              disabled={runningAI}
            >
              {runningAI && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Run AI Selection
            </Button>
          )}
          {event.status === 'selection_pending' && (
            <Button
              style={{ backgroundColor: '#006B3C' }}
              className="text-white hover:opacity-90"
              onClick={handleDispatch}
              disabled={generatingCodes}
            >
              {generatingCodes && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Dispatch Invitations & Generate Codes
            </Button>
          )}
          {event.status === 'invitations_sent' && (
            <Button style={{ backgroundColor: '#006B3C' }} className="text-white hover:opacity-90" onClick={() => updateEventStatus('active')}>
              Mark Event Active
            </Button>
          )}
          {event.status === 'active' && (
            <Button variant="secondary" style={{ backgroundColor: '#0D2240' }} className="text-white hover:opacity-90" onClick={() => setShowCompleteDialog(true)}>
              Complete Event
            </Button>
          )}
          <Button
            variant="ghost"
            className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-auto"
            onClick={() => setShowCancelDialog(true)}
          >
            Cancel Event
          </Button>
        </div>
      )}

      {canEdit && (
        <div className={cn('flex', event.status !== 'cancelled' && event.status !== 'completed' ? '' : 'justify-end')}>
          <Button
            variant="ghost"
            className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1.5"
            onClick={() => setShowDeleteDialog(true)}
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete Event
          </Button>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="setup">
        <TabsList>
          <TabsTrigger value="setup">Setup</TabsTrigger>
          <TabsTrigger value="ai">AI Recommendations</TabsTrigger>
          <TabsTrigger value="selection">Admin Selection</TabsTrigger>
          <TabsTrigger value="codes">Codes</TabsTrigger>
          <TabsTrigger value="attendance">Attendance</TabsTrigger>
          <TabsTrigger value="audit">Audit Log</TabsTrigger>
        </TabsList>

        <TabsContent value="setup" className="mt-6">
          <SetupTab event={event} eventId={eventId} canEdit={canEdit} onUpdated={refetchEvent} />
        </TabsContent>
        <TabsContent value="ai" className="mt-6">
          <AIRecommendationsTab
            eventId={eventId}
            event={event}
            canEdit={canEdit}
            onRunAI={handleRunAI}
            runningAI={runningAI}
          />
        </TabsContent>
        <TabsContent value="selection" className="mt-6">
          <CCOSelectionTab eventId={eventId} event={event} journalists={journalists ?? []} canEdit={canEdit} />
        </TabsContent>
        <TabsContent value="codes" className="mt-6">
          <CodesTab eventId={eventId} event={event} />
        </TabsContent>
        <TabsContent value="attendance" className="mt-6">
          <AttendanceTab eventId={eventId} event={event} journalists={journalists ?? []} />
        </TabsContent>
        <TabsContent value="audit" className="mt-6">
          <AuditLogTab eventId={eventId} />
        </TabsContent>
      </Tabs>

      {/* Complete confirmation */}
      <AlertDialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Complete this event?</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark the event as completed and record any no-shows. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              style={{ backgroundColor: '#0D2240' }}
              onClick={handleCompleteEvent}
              disabled={completingEvent}
            >
              {completingEvent && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Complete Event
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel confirmation */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this event?</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel the event. Journalists will not be notified automatically.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Go back</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={handleCancelEvent}
              disabled={cancelling}
            >
              {cancelling && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Cancel Event
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{event.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the event and any AI recommendations, admin selections, attendance codes, and audit history attached to it. This cannot be undone. If passes or accreditation requests have already been issued for this event, deletion will be blocked — cancel the event instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Go back</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDeleteEvent}
              disabled={deleting}
            >
              {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete Event
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// ── Tab 1: Setup ───────────────────────────────────────────────────────────

function SetupTab({ event, eventId, canEdit, onUpdated }: any) {
  const { toast } = useToast()
  const [saving, setSaving] = useState(false)
  const [fields, setFields] = useState({
    name: event.name ?? '',
    venue: event.venue ?? '',
    venue_city: event.venue_city ?? '',
    host_country: event.host_country ?? '',
    special_criteria: event.special_criteria ?? '',
  })

  async function save() {
    setSaving(true)
    try {
      const { error } = await (supabase.from('events' as any).update(fields).eq('id', eventId) as any)
      if (error) throw error
      toast({ title: 'Event updated' })
      onUpdated()
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-muted-foreground">Event Name</label>
            <Input value={fields.name} onChange={e => setFields(f => ({ ...f, name: e.target.value }))} className="mt-1" disabled={!canEdit} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Host Country</label>
            <Select value={fields.host_country} onValueChange={v => setFields(f => ({ ...f, host_country: v }))} disabled={!canEdit}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ECOWAS_COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Venue</label>
            <Input value={fields.venue} onChange={e => setFields(f => ({ ...f, venue: e.target.value }))} className="mt-1" disabled={!canEdit} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">City</label>
            <Input value={fields.venue_city} onChange={e => setFields(f => ({ ...f, venue_city: e.target.value }))} className="mt-1" disabled={!canEdit} />
          </div>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Special Criteria</label>
          <Textarea value={fields.special_criteria} onChange={e => setFields(f => ({ ...f, special_criteria: e.target.value }))} className="mt-1" rows={3} disabled={!canEdit} />
        </div>
        {canEdit && (
          <Button onClick={save} disabled={saving} style={{ backgroundColor: '#006B3C' }} className="text-white hover:opacity-90">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Changes
          </Button>
        )}
      </CardContent>
    </Card>
  )
}

// ── Tab 2: AI Recommendations ──────────────────────────────────────────────

function AIRecommendationsTab({ eventId, event, canEdit, onRunAI, runningAI }: any) {
  const { data: recs, refetch } = useQuery({
    queryKey: ['ai-recommendations', eventId],
    queryFn: async () => {
      const { data } = await (supabase
        .from('event_ai_recommendations' as any)
        .select(`
          id, rank, selection_score, attendance_type, reasoning_text, category, coverage_flag,
          journalists(id, first_name, last_name, nationality, beats, seniority,
            journalist_affiliations(is_primary, media_outlets(name))
          )
        `)
        .eq('event_id', eventId)
        .order('rank') as any)
      return (data || []) as any[]
    },
    enabled: !!eventId
  })

  const physical = (recs || []).filter((r: any) => r.attendance_type === 'physical').length
  const online = (recs || []).filter((r: any) => r.attendance_type === 'online').length
  const local = (recs || []).filter((r: any) => r.category === 'local').length
  const regional = (recs || []).filter((r: any) => r.category === 'regional').length

  const MAX_SELECTION_RUNS = 3
  const runsUsed = event?.selection_run_count ?? 0
  const runsRemaining = Math.max(0, MAX_SELECTION_RUNS - runsUsed)
  const limitReached = runsUsed >= MAX_SELECTION_RUNS

  const canRun = canEdit && ['draft', 'selection_pending'].includes(event?.status) && !limitReached

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
        <span className="text-amber-600 text-sm shrink-0">⚠</span>
        <p className="text-amber-700 text-xs text-justify">
          This AI-generated list is permanently recorded and will remain visible
          regardless of any changes made in the Admin Selection tab. It represents
          the system's recommendation before any human intervention.
        </p>
      </div>

      <div className="flex items-center gap-3">
        {canEdit && ['draft', 'selection_pending'].includes(event?.status) && (
          <Button
            style={{ backgroundColor: '#0D2240' }}
            className="text-white hover:opacity-90"
            onClick={async () => { await onRunAI(); refetch() }}
            disabled={runningAI || !canRun}
          >
            {runningAI
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analysing journalist pool…</>
              : runsUsed === 0 ? 'Run Selection Algorithm' : 'Refresh Selection'
            }
          </Button>
        )}
        {canEdit && ['draft', 'selection_pending'].includes(event?.status) && (
          <span className={cn('text-xs', limitReached ? 'text-destructive font-medium' : 'text-muted-foreground')}>
            {limitReached
              ? 'Refresh limit reached (3/3) — this keeps selection fair by preventing re-rolling for a preferred outcome.'
              : `${runsRemaining} of ${MAX_SELECTION_RUNS} refreshes remaining`}
          </span>
        )}
      </div>

      {(recs || []).length > 0 && (
        <p className="text-justify text-xs text-muted-foreground">
          {physical} physical · {online} online recommended
          {(local > 0 || regional > 0) && ` (${local} local, ${regional} regional)`}
        </p>
      )}

      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow style={{ backgroundColor: '#0D2240' }}>
              {['Rank', 'Journalist', 'Category', 'Outlet', 'Beat', 'Score', 'Type', 'AI Reasoning'].map(h => (
                <TableHead key={h} style={{ color: '#F0E4B8' }} className="font-semibold text-xs">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {(recs || []).length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground text-sm">
                No recommendations yet. Run the selection algorithm to generate them.
              </TableCell></TableRow>
            ) : (recs || []).map((r: any, idx: number) => {
              const j = r.journalists as any
              const primaryAff = (j?.journalist_affiliations || []).find((a: any) => a.is_primary)
              const goldRanks = [0, 1, 2]
              return (
                <TableRow key={r.id} className={idx % 2 === 1 ? 'bg-[#F7F5F0]' : ''}>
                  <TableCell>
                    <span className={cn('font-bold text-sm', goldRanks.includes(idx) && 'text-[#C8A84B]')}>
                      #{r.rank}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium text-sm flex items-center gap-1.5">
                      {j?.first_name} {j?.last_name}
                      {r.coverage_flag === 'none' && (
                        <span title="No coverage of the institution on file" className="inline-flex items-center gap-0.5 text-[10px] font-medium text-red-700 bg-red-50 px-1.5 py-0.5 rounded-full">
                          <AlertTriangle className="w-2.5 h-2.5" /> No coverage
                        </span>
                      )}
                      {r.coverage_flag === 'below_average' && (
                        <span title="Coverage lags behind the pool average" className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full">
                          <AlertTriangle className="w-2.5 h-2.5" /> Below avg.
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">{j?.nationality}</div>
                  </TableCell>
                  <TableCell>
                    {r.category && (
                      <Badge variant="outline" className={`text-[10px] capitalize ${r.category === 'local' ? 'bg-purple-50 text-purple-700' : 'bg-amber-50 text-amber-700'}`}>
                        {r.category}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{primaryAff?.media_outlets?.name ?? ''}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{j?.beats ?? ''}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#006B3C] rounded-full"
                          style={{ width: `${Math.round((r.selection_score ?? 0) * 100)}%` }}
                        />
                      </div>
                      <span className="text-xs">{Math.round((r.selection_score ?? 0) * 100)}%</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-xs capitalize ${r.attendance_type === 'online' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}`}>
                      {r.attendance_type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs italic text-muted-foreground max-w-[200px]">
                    {r.reasoning_text ?? ''}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

// ── Tab 3: CCO Selection ───────────────────────────────────────────────────

function CCOSelectionTab({ eventId, event, journalists, canEdit }: any) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [overrideTarget, setOverrideTarget] = useState<{ selId: string; jId: string; name: string } | null>(null)
  const [overrideCategory, setOverrideCategory] = useState('')
  const [overrideNotes, setOverrideNotes] = useState('')
  const [savingOverride, setSavingOverride] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [addSearch, setAddSearch] = useState('')
  const [togglingSelId, setTogglingSelId] = useState<string | null>(null)
  const [addingJournalist, setAddingJournalist] = useState(false)

  const { data: recs } = useQuery({
    queryKey: ['ai-recommendations', eventId],
    queryFn: async () => {
      const { data } = await (supabase.from('event_ai_recommendations' as any).select('*, journalists(id, first_name, last_name, journalist_affiliations(is_primary, media_outlets(name)))').eq('event_id', eventId).order('rank') as any)
      return (data || []) as any[]
    }
  })

  const { data: selections, refetch: refetchSel } = useQuery({
    queryKey: ['event-selections', eventId],
    queryFn: async () => {
      const { data } = await (supabase.from('event_selections' as any).select('id, journalist_id, attendance_type, source, status, override_reason_category, override_notes, journalists(id, first_name, last_name, journalist_affiliations(is_primary, media_outlets(name)))').eq('event_id', eventId) as any)
      return (data || []) as any[]
    }
  })

  const selJournalistIds = new Set((selections || []).map((s: any) => s.journalist_id))
  const physSel = (selections || []).filter((s: any) => s.attendance_type === 'physical' && s.status !== 'overridden').length
  const onlineSel = (selections || []).filter((s: any) => s.attendance_type === 'online' && s.status !== 'overridden').length

  async function toggleAttendanceType(selId: string, current: string) {
    if (togglingSelId) return
    setTogglingSelId(selId)
    const next = current === 'physical' ? 'online' : 'physical'
    try {
      const { error } = await (supabase.from('event_selections' as any).update({ attendance_type: next }).eq('id', selId) as any)
      if (error) throw error
      refetchSel()
    } catch (e: any) {
      toast({ title: 'Failed to update attendance type', description: e.message, variant: 'destructive' })
    } finally {
      setTogglingSelId(null)
    }
  }

  async function confirmOverride() {
    if (!overrideTarget || !overrideCategory) return
    setSavingOverride(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const { error: updateError } = await (supabase.from('event_selections' as any).update({
        status: 'overridden',
        override_reason_category: overrideCategory,
        override_notes: overrideNotes,
        decided_by: user?.id,
        decided_at: new Date().toISOString(),
      }).eq('id', overrideTarget.selId) as any)
      if (updateError) throw updateError

      const { error: auditError } = await (supabase.from('event_audit_log' as any).insert({
        event_id: eventId,
        org_id: event.org_id,
        actor_id: user?.id,
        action: 'cco_override',
        payload: { journalist_id: overrideTarget.jId, journalist_name: overrideTarget.name, reason: overrideCategory, notes: overrideNotes }
      }) as any)
      if (auditError) throw auditError

      refetchSel()
      queryClient.invalidateQueries({ queryKey: ['audit-log', eventId] })
      toast({ title: 'Override recorded', description: 'The AI recommendation list is unchanged.' })
      setOverrideTarget(null)
      setOverrideCategory('')
      setOverrideNotes('')
    } catch (e: any) {
      toast({ title: 'Failed to record override', description: e.message, variant: 'destructive' })
    } finally {
      setSavingOverride(false)
    }
  }

  async function addJournalist(journalistId: string) {
    if (addingJournalist) return
    setAddingJournalist(true)
    try {
      const j = journalists.find((x: any) => x.id === journalistId)
      const { data: { user } } = await supabase.auth.getUser()
      const { error: insertError } = await (supabase.from('event_selections' as any).insert({
        event_id: eventId, journalist_id: journalistId, attendance_type: 'physical',
        source: 'cco_added', status: 'selected', decided_by: user?.id, decided_at: new Date().toISOString()
      }) as any)
      if (insertError) throw insertError

      const { error: auditError } = await (supabase.from('event_audit_log' as any).insert({
        event_id: eventId, org_id: event.org_id, actor_id: user?.id, action: 'cco_add',
        payload: { journalist_id: journalistId, journalist_name: j ? `${j.first_name} ${j.last_name}` : journalistId }
      }) as any)
      if (auditError) throw auditError

      refetchSel()
      queryClient.invalidateQueries({ queryKey: ['audit-log', eventId] })
      toast({ title: 'Journalist added to the event.' })
      setAddOpen(false)
    } catch (e: any) {
      toast({ title: 'Failed to add journalist', description: e.message, variant: 'destructive' })
    } finally {
      setAddingJournalist(false)
    }
  }

  const filteredAddList = (journalists || []).filter((j: any) =>
    !selJournalistIds.has(j.id) &&
    `${j.first_name} ${j.last_name} ${j.outlet_name ?? ''}`.toLowerCase().includes(addSearch.toLowerCase())
  )

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: AI Recommended (read-only reference) */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">AI Recommended</h3>
            <Badge variant="secondary" className="text-xs">Reference Only</Badge>
          </div>
          <div className="space-y-1.5 opacity-60">
            {(recs || []).map((r: any) => {
              const j = r.journalists as any
              const primaryAff = (j?.journalist_affiliations || []).find((a: any) => a.is_primary)
              return (
                <div key={r.id} className="flex items-center gap-2 py-1.5 px-3 rounded-lg border bg-muted/40 text-sm">
                  <span className="text-xs text-muted-foreground w-6">#{r.rank}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{j?.first_name} {j?.last_name}</div>
                    <div className="text-xs text-muted-foreground truncate">{primaryAff?.media_outlets?.name}</div>
                  </div>
                  <Badge variant="outline" className="text-xs capitalize shrink-0">{r.attendance_type}</Badge>
                </div>
              )
            })}
            {(recs || []).length === 0 && (
              <p className="text-justify text-xs text-muted-foreground py-4 text-center">Run AI selection first.</p>
            )}
          </div>
        </div>

        {/* Right: Final Selection (editable) */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">Final Selection</h3>
            <span className={cn('text-xs', physSel > (event.physical_capacity ?? 999) ? 'text-red-600' : 'text-muted-foreground')}>
              Physical {physSel}/{event.physical_capacity ?? '∞'}
            </span>
            <span className={cn('text-xs', onlineSel > (event.online_capacity ?? 999) ? 'text-red-600' : 'text-muted-foreground')}>
              Online {onlineSel}/{event.online_capacity ?? '∞'}
            </span>
          </div>
          <div className="space-y-1.5">
            {(selections || []).map((s: any) => {
              const j = s.journalists as any
              const primaryAff = (j?.journalist_affiliations || []).find((a: any) => a.is_primary)
              const name = `${j?.first_name ?? ''} ${j?.last_name ?? ''}`.trim()
              return (
                <div key={s.id} className={cn('flex items-center gap-2 py-1.5 px-3 rounded-lg border text-sm', s.status === 'overridden' && 'opacity-50')}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{name}</div>
                    <div className="text-xs text-muted-foreground truncate">{primaryAff?.media_outlets?.name}</div>
                    {s.status === 'overridden' && s.override_reason_category && (
                      <div className="text-xs text-amber-600 italic">{s.override_reason_category}</div>
                    )}
                  </div>
                  <Badge
                    variant="outline"
                    className={cn('text-xs select-none',
                      togglingSelId === s.id ? 'opacity-50 cursor-wait' : 'cursor-pointer',
                      s.status === 'overridden' ? 'bg-amber-50 text-amber-700'
                      : s.source === 'cco_added' ? 'bg-blue-50 text-blue-700'
                      : 'bg-green-50 text-green-700'
                    )}
                    onClick={() => canEdit && s.status !== 'overridden' && togglingSelId !== s.id && toggleAttendanceType(s.id, s.attendance_type)}
                    title={canEdit && s.status !== 'overridden' ? 'Click to toggle Physical ↔ Online' : undefined}
                  >
                    {s.status === 'overridden' ? 'Overridden'
                     : s.source === 'cco_added' ? 'Admin Added'
                     : 'AI Recommended'}
                  </Badge>
                  {canEdit && s.status !== 'overridden' && (
                    <button
                      className="text-red-500 hover:text-red-700 text-xs"
                      onClick={() => setOverrideTarget({ selId: s.id, jId: j?.id ?? '', name })}
                      title="Override / Remove"
                    >
                      ✕
                    </button>
                  )}
                </div>
              )
            })}
            {(selections || []).length === 0 && (
              <p className="text-justify text-xs text-muted-foreground py-4 text-center">No selections yet.</p>
            )}
          </div>

          {canEdit && (
            <Popover open={addOpen} onOpenChange={setAddOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="w-full mt-2">
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Add Journalist
                  <ChevronsUpDown className="ml-auto w-3.5 h-3.5 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[360px] p-0">
                <Command>
                  <CommandInput placeholder="Search journalist…" value={addSearch} onValueChange={setAddSearch} />
                  <CommandList>
                    <CommandEmpty>No journalist found.</CommandEmpty>
                    <CommandGroup>
                      {filteredAddList.map((j: any) => (
                        <CommandItem key={j.id} onSelect={() => addJournalist(j.id)}>
                          <div>
                            <div className="font-medium">{j.first_name} {j.last_name}</div>
                            <div className="text-xs text-muted-foreground">{j.outlet_name}, {j.nationality}</div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>

      {/* Override dialog */}
      <Dialog open={!!overrideTarget} onOpenChange={o => !o && setOverrideTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Override AI Recommendation</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground text-justify">
            You are removing <strong>{overrideTarget?.name}</strong> from the AI-recommended
            selection. This action is permanently recorded in the event audit log.
          </p>
          <Select value={overrideCategory} onValueChange={setOverrideCategory}>
            <SelectTrigger><SelectValue placeholder="Select reason…" /></SelectTrigger>
            <SelectContent>
              {OVERRIDE_CATEGORIES.map(c => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            placeholder="Additional notes (required if Other is selected)"
            value={overrideNotes}
            onChange={e => setOverrideNotes(e.target.value)}
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideTarget(null)}>Cancel</Button>
            <Button
              disabled={!overrideCategory || (overrideCategory === 'other' && !overrideNotes) || savingOverride}
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={confirmOverride}
            >
              {savingOverride && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirm Override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ── Tab 4: Codes ───────────────────────────────────────────────────────────

function CodesTab({ eventId, event }: any) {
  const [copied, setCopied] = useState(false)

  const statusIdx = STATUS_STEPS.indexOf(event?.status)
  const codesReady = statusIdx >= STATUS_STEPS.indexOf('invitations_sent')

  const { data: stationCodes } = useQuery({
    queryKey: ['station-codes', eventId],
    queryFn: async () => {
      const { data } = await (supabase
        .from('event_station_codes' as any)
        .select('*, media_outlets(name, country)')
        .eq('event_id', eventId) as any)
      return (data || []) as any[]
    },
    enabled: codesReady
  })

  const onlineCode = event?.online_code ?? null

  function copyOnlineCode() {
    if (!onlineCode) return
    navigator.clipboard.writeText(onlineCode)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (!codesReady) return (
    <Card><CardContent className="py-12 text-center text-muted-foreground">
      <p className="text-justify text-sm">Codes are generated when invitations are dispatched.</p>
    </CardContent></Card>
  )

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-semibold mb-3">Station Codes (per outlet)</h3>
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow style={{ backgroundColor: '#0D2240' }}>
                {['Outlet', 'Code', 'Status'].map(h => (
                  <TableHead key={h} style={{ color: '#F0E4B8' }} className="font-semibold">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {(stationCodes || []).length === 0 ? (
                <TableRow><TableCell colSpan={3} className="text-center py-8 text-muted-foreground text-sm">No station codes found.</TableCell></TableRow>
              ) : (stationCodes || []).map((sc: any, idx: number) => (
                <TableRow key={sc.id} className={idx % 2 === 1 ? 'bg-[#F7F5F0]' : ''}>
                  <TableCell className="font-medium">{sc.media_outlets?.name ?? ''}</TableCell>
                  <TableCell>
                    <span className="font-mono font-semibold text-sm" style={{ color: '#006B3C' }}>
                      {sc.code}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">
                    {sc.used_by_journalist_id
                      ? <span className="text-green-600">✅ Used</span>
                      : <span className="text-muted-foreground">⏳ Not yet used</span>
                    }
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {onlineCode && (
        <div>
          <h3 className="text-sm font-semibold mb-3">Online Attendance Code</h3>
          <div className="flex items-center gap-3 p-4 rounded-lg border bg-muted/30">
            <span className="font-mono text-xl font-bold tracking-wider" style={{ color: '#006B3C' }}>
              {onlineCode}
            </span>
            <Button size="sm" variant="outline" onClick={copyOnlineCode} className="ml-auto">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Tab 5: Attendance ──────────────────────────────────────────────────────

function AttendanceTab({ eventId, event, journalists }: any) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [manualCode, setManualCode] = useState('')
  const [selectedJournalistForManual, setSelectedJournalistForManual] = useState<string>('')
  const [journalistSearchOpen, setJournalistSearchOpen] = useState(false)
  const [journalistSearch, setJournalistSearch] = useState('')
  const [recording, setRecording] = useState(false)

  const { data: selections } = useQuery({
    queryKey: ['attendance-selections', eventId],
    queryFn: async () => {
      const { data } = await (supabase
        .from('event_selections' as any)
        .select('id, journalist_id, attendance_type, status, journalists(id, first_name, last_name, journalist_affiliations(is_primary, media_outlets(name)))')
        .eq('event_id', eventId) as any)
      return (data || []) as any[]
    }
  })

  const { data: attendanceRecords, refetch: refetchAttendance } = useQuery({
    queryKey: ['attendance-records', eventId],
    queryFn: async () => {
      const { data } = await (supabase
        .from('attendance_records' as any)
        .select('*')
        .eq('event_id', eventId) as any)
      return (data || []) as any[]
    }
  })

  const attendanceMap = new Map<string, any>()
  ;(attendanceRecords || []).forEach((r: any) => attendanceMap.set(r.journalist_id, r))

  async function recordManualAttendance() {
    if (!manualCode) return
    setRecording(true)
    try {
      const result = await supabase.functions.invoke('submit-attendance-code', {
        body: {
          event_id: eventId,
          code: manualCode,
          journalist_id: selectedJournalistForManual || undefined,
        }
      })
      if (result.error) throw new Error(result.error.message)
      const body = result.data as any
      if (body?.error) throw new Error(body.error)
      toast({ title: body?.message ?? 'Attendance recorded' })
      setManualCode('')
      setSelectedJournalistForManual('')
      refetchAttendance()
      queryClient.invalidateQueries({ queryKey: ['audit-log', eventId] })
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' })
    } finally {
      setRecording(false)
    }
  }

  const physAttended = (attendanceRecords || []).filter((r: any) => r.attendance_type === 'physical').length
  const onlineAttended = (attendanceRecords || []).filter((r: any) => r.attendance_type === 'online').length
  const noshows = (selections || []).filter((s: any) => !attendanceMap.has(s.journalist_id)).length

  const CREDIT_CONFIG: Record<string, { label: string; className: string }> = {
    full: { label: 'Full', className: 'bg-green-100 text-green-800 border-green-300' },
    partial: { label: 'Partial', className: 'bg-amber-100 text-amber-800 border-amber-300' },
    none: { label: 'None', className: 'bg-gray-100 text-gray-600 border-gray-300' },
  }

  const selectedJournalistName = selectedJournalistForManual
    ? (() => { const j = journalists.find((x: any) => x.id === selectedJournalistForManual); return j ? `${j.first_name} ${j.last_name}` : '' })()
    : ''

  return (
    <div className="space-y-4">
      {/* Manual code entry */}
      <div className="flex gap-2 flex-wrap">
        <Input
          placeholder="Enter attendance code (EP-…)"
          value={manualCode}
          onChange={e => setManualCode(e.target.value.toUpperCase())}
          className="font-mono max-w-xs"
        />
        <Popover open={journalistSearchOpen} onOpenChange={setJournalistSearchOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-48 justify-between">
              {selectedJournalistName || 'Select journalist…'}
              <ChevronsUpDown className="ml-2 w-4 h-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0">
            <Command>
              <CommandInput placeholder="Search…" value={journalistSearch} onValueChange={setJournalistSearch} />
              <CommandList>
                <CommandEmpty>No journalist found.</CommandEmpty>
                <CommandGroup>
                  {(journalists || [])
                    .filter((j: any) => `${j.first_name} ${j.last_name}`.toLowerCase().includes(journalistSearch.toLowerCase()))
                    .map((j: any) => (
                      <CommandItem key={j.id} onSelect={() => { setSelectedJournalistForManual(j.id); setJournalistSearchOpen(false) }}>
                        {j.first_name} {j.last_name}
                        {selectedJournalistForManual === j.id && <Check className="ml-auto w-4 h-4" />}
                      </CommandItem>
                    ))
                  }
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <Button
          onClick={recordManualAttendance}
          disabled={!manualCode || recording}
          style={{ backgroundColor: '#006B3C' }}
          className="text-white hover:opacity-90"
        >
          {recording && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Record Attendance
        </Button>
      </div>

      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow style={{ backgroundColor: '#0D2240' }}>
              {['Journalist', 'Expected', 'Code Used', 'Submitted At', 'Credit'].map(h => (
                <TableHead key={h} style={{ color: '#F0E4B8' }} className="font-semibold">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {(selections || []).length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground text-sm">No selections yet.</TableCell></TableRow>
            ) : (selections || []).map((s: any, idx: number) => {
              const j = s.journalists as any
              const primaryAff = (j?.journalist_affiliations || []).find((a: any) => a.is_primary)
              const att = attendanceMap.get(s.journalist_id)
              const isNoshow = !att && event?.status === 'completed'
              const creditConf = att?.credit_type ? CREDIT_CONFIG[att.credit_type] : null
              return (
                <TableRow key={s.id} className={cn(idx % 2 === 1 ? 'bg-[#F7F5F0]' : '', isNoshow && 'bg-red-50')}>
                  <TableCell>
                    <div className="font-medium text-sm">{j?.first_name} {j?.last_name}</div>
                    <div className="text-xs text-muted-foreground">{primaryAff?.media_outlets?.name}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs capitalize">{s.attendance_type}</Badge>
                  </TableCell>
                  <TableCell>
                    {att?.code_used
                      ? <span className="font-mono text-xs">{att.code_used}</span>
                      : <span className="text-muted-foreground"></span>
                    }
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {att?.submitted_at
                      ? new Date(att.submitted_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })
                      : ''
                    }
                  </TableCell>
                  <TableCell>
                    {isNoshow
                      ? <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-300">No-show</Badge>
                      : creditConf
                      ? <Badge variant="outline" className={`text-xs ${creditConf.className}`}>{creditConf.label}</Badge>
                      : <Badge variant="outline" className="text-xs border-dashed border-blue-300 text-blue-600">Pending</Badge>
                    }
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      <p className="text-justify text-xs text-muted-foreground">
        ✅ {physAttended} physical attended · 🖥 {onlineAttended} online attended · ❌ {noshows} no-shows
      </p>
    </div>
  )
}

// ── Tab 6: Audit Log ───────────────────────────────────────────────────────

function AuditLogTab({ eventId }: { eventId: string }) {
  const [expandedRow, setExpandedRow] = useState<string | null>(null)

  const { data: auditLog } = useQuery({
    queryKey: ['audit-log', eventId],
    queryFn: async () => {
      const { data } = await (supabase
        .from('event_audit_log' as any)
        .select('*, profiles(full_name)')
        .eq('event_id', eventId)
        .order('created_at', { ascending: true }) as any)
      return (data || []) as any[]
    }
  })

  function exportCSV() {
    if (!auditLog?.length) return
    const rows = [
      ['Timestamp', 'Action', 'Actor', 'Payload'],
      ...(auditLog || []).map((l: any) => [
        new Date(l.created_at).toISOString(),
        l.action,
        l.profiles?.full_name ?? l.actor_id ?? '',
        JSON.stringify(l.payload ?? {}),
      ])
    ]
    const csv = rows.map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `event-audit-${eventId}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={exportCSV} disabled={!auditLog?.length}>
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {(!auditLog || auditLog.length === 0) ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground text-sm">No audit log entries yet.</CardContent></Card>
      ) : (
        <div className="space-y-1">
          {(auditLog || []).map((l: any) => {
            const display = ACTION_DISPLAY[l.action] ?? { icon: '📄', label: l.action, colour: 'text-slate-600' }
            const isExpanded = expandedRow === l.id
            return (
              <div key={l.id} className="flex flex-col rounded-lg border hover:bg-muted/30 transition-colors">
                <button
                  className="flex items-center gap-3 px-3 py-2.5 text-left"
                  onClick={() => setExpandedRow(isExpanded ? null : l.id)}
                >
                  <span className="text-[10px] text-muted-foreground w-32 shrink-0">
                    {new Date(l.created_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}
                  </span>
                  <span className="text-sm">{display.icon}</span>
                  <span className={`text-sm font-medium ${display.colour}`}>{display.label}</span>
                  <span className="text-xs text-muted-foreground ml-auto">
                    {l.profiles?.full_name ?? ''}
                  </span>
                </button>
                {isExpanded && l.payload && (
                  <pre className="text-xs bg-muted/50 rounded-b-lg px-4 py-2 overflow-x-auto border-t font-mono">
                    {JSON.stringify(l.payload, null, 2)}
                  </pre>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
